import { useState } from 'react'
import { motion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'

type EventGalleryProps = {
  title: string
  subtitle?: string
  images: { src: string; alt?: string }[]
  initialCount?: number
  loadMoreCount?: number
}

export default function EventGallery({ 
  title, 
  subtitle, 
  images, 
  initialCount = 8,
  loadMoreCount = 8 
}: EventGalleryProps) {
  const [displayCount, setDisplayCount] = useState(initialCount)
  const hasImages = images && images.length > 0
  const displayedImages = images.slice(0, displayCount)
  const hasMore = displayCount < images.length

  return (
    <section className="py-24 md:py-32 border-t border-white/5 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
      <div className="max-w-[1400px] mx-auto px-6 relative">
        {/* Hintergrund-Glow */}
        <div className="pointer-events-none absolute -top-10 right-10 w-[420px] h-[420px] bg-brand-800/8 rounded-full blur-[120px]" />

        {/* Header */}
        <div className="relative text-center mb-14">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
          >
            Impressionen
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light mb-4"
          >
            {title}
          </motion.h2>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/50 max-w-2xl mx-auto text-sm md:text-base leading-relaxed"
            >
              {subtitle}
            </motion.p>
          )}
        </div>

        {/* Galerie */}
        {!hasImages ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative border border-dashed border-white/10 rounded-2xl py-16 px-6 text-center"
          >
            <p className="text-white/40 text-sm md:text-base">
              Bildimpressionen folgen in Kürze. Sprechen Sie uns gern an – wir zeigen Ihnen Beispiele aus
              vergangenen Veranstaltungen.
            </p>
          </motion.div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            >
              {displayedImages.map((image, index) => (
                <motion.div
                  key={image.src + index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.05, 0.4) }}
                  className="group relative overflow-hidden rounded-xl border border-white/5 hover:border-brand-800/40 bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-500"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={image.src}
                      alt={image.alt || title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </motion.div>
              ))}
            </motion.div>

            {/* Mehr laden Button */}
            {hasMore && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex justify-center mt-12"
              >
                <motion.button
                  onClick={() => setDisplayCount(prev => Math.min(prev + loadMoreCount, images.length))}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-8 py-3 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-brand-800/50 text-white text-sm uppercase tracking-[0.2em] font-semibold rounded-full transition-all duration-500 flex items-center gap-2"
                >
                  Mehr laden
                  <ChevronDown className="w-4 h-4" />
                </motion.button>
              </motion.div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
